import { clsx, type ClassValue } from 'clsx';
import { twMerge } from 'tailwind-merge';

/** Склейка классов с разрешением конфликтов Tailwind (последний выигрывает). */
export function cn(...inputs: ClassValue[]): string {
  return twMerge(clsx(inputs));
}

/** Уникальный идентификатор для aria-связей вне React 18+ useId (например, в утилитах). */
let idCounter = 0;
export function uid(prefix = 'wf'): string {
  idCounter += 1;
  return `${prefix}-${idCounter}`;
}

export function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

export function clamp(value: number, min: number, max: number): number {
  return Math.min(Math.max(value, min), max);
}

export function isNotNullish<T>(value: T | null | undefined): value is T {
  return value !== null && value !== undefined;
}

/** Группировка массива по ключу — используется в канбане и сводках. */
export function groupBy<T, K extends string | number>(items: readonly T[], key: (item: T) => K): Record<K, T[]> {
  const result = {} as Record<K, T[]>;
  for (const item of items) {
    const k = key(item);
    (result[k] ??= []).push(item);
  }
  return result;
}

export function sum(values: readonly number[]): number {
  return values.reduce((acc, value) => acc + value, 0);
}

export function unique<T>(items: readonly T[]): T[] {
  return Array.from(new Set(items));
}

/** Простая задержка вызова: поиск, автосохранение фильтров. */
export function debounce<A extends unknown[]>(fn: (...args: A) => void, delay = 300): ((...args: A) => void) & {
  cancel: () => void;
} {
  let timer: ReturnType<typeof setTimeout> | undefined;
  const debounced = (...args: A) => {
    if (timer) clearTimeout(timer);
    timer = setTimeout(() => fn(...args), delay);
  };
  debounced.cancel = () => {
    if (timer) clearTimeout(timer);
  };
  return debounced;
}

/** Нормализация строки для поиска без учёта регистра и ё/е. */
export function normalizeSearch(value: string): string {
  return value.toLowerCase().replace(/ё/g, 'е').trim();
}

/** Инициалы для аватара: «Ахметов Данияр» → «АД». */
export function initials(name: string): string {
  const parts = name.trim().split(/\s+/).slice(0, 2);
  return parts.map((part) => part.charAt(0).toUpperCase()).join('');
}
